import * as i0 from '@angular/core';
import { InjectionToken, Injectable, inject, NgModule } from '@angular/core';
import { createServiceApi, serviceTypes, toHttpParams, createNamedApi, combineEffectsWithProviders } from '@essent/common';
export { Channel, DirectionTariff, EnergyType, IncomingPaymentMethod, PreferredChannel, ProductDuration, SegmentType } from '@essent/common';
import * as i1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { snakeCase, mapWhenDefined } from '@essent/core-utils';

const NEW_CUSTOMER_PATH = 'contracting/newcustomer';

const apiVersion$D = '2';
const getAdditionalInformationApi = createServiceApi('[NewCustomerState] Get additional information', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/additionalinformation/v${apiVersion$D}`,
    version: apiVersion$D,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$C = '2';
const putAdditionalInformationApi = createServiceApi('[NewCustomerState] Put additional information', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/additionalinformation/v${apiVersion$C}`,
    version: apiVersion$C,
    getBody: payload => payload
});

const apiVersion$B = '2';
const getBudgetBillApi = createServiceApi('[NewCustomerState] Get budget bill', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/budgetbillamount/v${apiVersion$B}`,
    version: apiVersion$B,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$A = '2';
const putBudgetBillApi = createServiceApi('[NewCustomerState] Put budget bill', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/budgetbillamount/v${apiVersion$A}`,
    version: apiVersion$A,
    getBody: payload => payload
});

const apiVersion$z = '2';
const getCampaignsApi = createServiceApi('[NewCustomerState] Get campaigns', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/campaigns/v${apiVersion$z}`,
    version: apiVersion$z,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$y = '2';
const putConfirmationApi = createServiceApi('[NewCustomerState] Put confirmation', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/confirmation/v${apiVersion$y}`,
    version: apiVersion$y,
    getHeaders: payload => {
        const headers = { Accept: 'application/json' };
        if (payload.payload?.extPartnerId) {
            headers['external-partner-id'] = payload.payload.extPartnerId;
        }
        return headers;
    },
    getBody: payload => {
        const { extPartnerId, ...rest } = payload.payload || {};
        return { ...payload, payload: rest };
    }
});

const apiVersion$x = '2';
const getConsentApi = createServiceApi('[NewCustomerState] Get consent', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/marketdataconsent/v${apiVersion$x}`,
    version: apiVersion$x,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$w = '2';
const putConsentApi = createServiceApi('[NewCustomerState] Put consent', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/marketdataconsent/v${apiVersion$w}`,
    version: apiVersion$w,
    getBody: payload => payload
});

const apiVersion$v = '2';
const getConsumptionApi = createServiceApi('[NewCustomerState] Get consumption', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/consumption/v${apiVersion$v}`,
    version: apiVersion$v,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$u = '2';
const putConsumptionApi = createServiceApi('[NewCustomerState] Put consumption', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/consumption/v${apiVersion$u}`,
    version: apiVersion$u,
    getBody: payload => payload
});

const apiVersion$t = '2';
const getCorrespondenceDetailsApi = createServiceApi('[NewCustomerState] Get correspondence details', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/correspondencedetails/v${apiVersion$t}`,
    version: apiVersion$t,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$s = '2';
const putCorrespondenceDetailsApi = createServiceApi('[NewCustomerState] Put correspondence details', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/correspondencedetails/v${apiVersion$s}`,
    version: apiVersion$s,
    getBody: payload => payload
});

const apiVersion$r = '1';
const getCreditCheckConsentApi = createServiceApi('[NewCustomerState] Get credit check consent', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/creditcheckconsent/v${apiVersion$r}`,
    version: apiVersion$r,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$q = '1';
const updateCreditCheckConsentApi = createServiceApi('[NewCustomerState] Update credit check consent', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/creditcheckconsent/v${apiVersion$q}`,
    version: apiVersion$q,
    getBody: payload => payload
});

const apiVersion$p = '1';
const getDepositApi = createServiceApi('[NewCustomerState] Get deposit', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/deposit/v${apiVersion$p}`,
    version: apiVersion$p,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$o = '1';
const updateDepositApi = createServiceApi('[NewCustomerState] Update deposit', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/deposit/v${apiVersion$o}`,
    version: apiVersion$o,
    getBody: payload => payload
});

const apiVersion$n = '1';
const getDuplicateCustomerApi = createServiceApi('[NewCustomerState] Get duplicate customer', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/checkduplicatecustomer/v${apiVersion$n}`,
    version: apiVersion$n,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$m = '1';
const putDuplicateCustomerApi = createServiceApi('[NewCustomerState] Put duplicate customer', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/checkduplicatecustomer/v${apiVersion$m}`,
    version: apiVersion$m,
    getBody: payload => payload
});

const apiVersion$l = '2';
const putEanDetailsApi = createServiceApi('[NewCustomerState] Put ean details', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/eandetails/v${apiVersion$l}`,
    version: apiVersion$l,
    getBody: payload => payload
});

const apiVersion$k = '1';
const putExtendTimeToLiveApi = createServiceApi('[NewCustomerState] Put extend time to live', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/extendtimetolive/v${apiVersion$k}`,
    version: apiVersion$k,
    getBody: payload => payload
});

const apiVersion$j = '2';
const getInstallationsApi = createServiceApi('[NewCustomerState] Get installations', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/installationdetails/v${apiVersion$j}`,
    version: apiVersion$j,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$i = '2';
const getMarketConsumptionApi = createServiceApi('[NewCustomerState] Get market consumption', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/marketconsumption/v${apiVersion$i}`,
    version: apiVersion$i,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$h = '1';
const getMetaDataApi = createServiceApi('[NewCustomerState] Get meta data', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/metadata/v${apiVersion$h}`,
    version: apiVersion$h,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$g = '1';
const putMetaDataApi = createServiceApi('[NewCustomerState] Put meta data', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/metadata/v${apiVersion$g}`,
    version: apiVersion$g,
    getBody: payload => payload
});

const apiVersion$f = '2';
const getOffersApi = createServiceApi('[NewCustomerState] Get offers', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/offers/v${apiVersion$f}`,
    version: apiVersion$f,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

var ChoiceOfTariff;
(function (ChoiceOfTariff) {
    ChoiceOfTariff["STANDARD"] = "standard";
    ChoiceOfTariff["PLUS"] = "plus";
    ChoiceOfTariff["BUDGET"] = "budget";
})(ChoiceOfTariff || (ChoiceOfTariff = {}));
var ConsentStatus;
(function (ConsentStatus) {
    ConsentStatus["GIVEN"] = "given";
    ConsentStatus["REJECTED"] = "rejected";
})(ConsentStatus || (ConsentStatus = {}));
var UsageType;
(function (UsageType) {
    UsageType["NORMAL_SUPPLY"] = "KVB";
    UsageType["LARGE_SUPPLY"] = "GVB";
    UsageType["GENERAL_SUPPLY_LARGE"] = "ART";
})(UsageType || (UsageType = {}));

const API = new InjectionToken('api-new-customer');

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
// https://wiki.essent.nl/confluence/display/SO/Functional+Error+Codes
var ResponseCodes;
(function (ResponseCodes) {
    ResponseCodes["GENERIC_SUCCESS"] = "200";
    ResponseCodes["EMAIL_NOT_FOUND"] = "1001";
    ResponseCodes["DOB_INVALID"] = "1002";
    ResponseCodes["CORRESPONDANCE_ADDRESS_NOT_FOUND"] = "1003";
    ResponseCodes["INVOICE_ADDRESS_NOT_FOUND"] = "1004";
    ResponseCodes["INVALID_IBAN"] = "1005";
    ResponseCodes["BLACKLISTED_IBAN"] = "1006";
    ResponseCodes["CONSENT_NOT_PRESENT_SWITCH_IN"] = "1007";
    ResponseCodes["QUESTIONNAIRE_NOT_FOUND"] = "1007";
    ResponseCodes["STEP_NOT_APPLICABLE"] = "1008";
    ResponseCodes["CONSENT_NOT_PRESENT_MOVE_IN"] = "1008";
    ResponseCodes["START_DATE_NOT_IN_LIMITS"] = "1009";
    ResponseCodes["CAMPAIGN_ID_INVALID"] = "1010";
    ResponseCodes["ELECTRICITY_TARRIFF_INVALID"] = "1011";
    ResponseCodes["GAS_TARRIFF_INVALID"] = "1012";
    ResponseCodes["TARIFF_COMBINATION_INVALID"] = "1013";
    ResponseCodes["BUDGET_BILL_AMOUNT_INVALID"] = "1014";
    ResponseCodes["INVALID_CUSTOMER_KEY"] = "1015";
    ResponseCodes["IBAN_OR_DOB_POPULATED"] = "1024";
    ResponseCodes["EAN_ERROR"] = "1032";
    ResponseCodes["CAR_IS_DOWN"] = "2001";
    ResponseCodes["NO_FLOW_ID"] = "9001";
})(ResponseCodes || (ResponseCodes = {}));
var ResponseMessages;
(function (ResponseMessages) {
    ResponseMessages["FLOW_ID_EXISTS"] = "Flow Id exists";
    ResponseMessages["HOUSE_NUMBER_EXTENSION_NOT_FOUND"] = "House number extension not found in CAR";
    ResponseMessages["NO_EAN_FOUND"] = "No EAN found in CAR";
    ResponseMessages["MULTIPLE_EAN_ELECTRICITY"] = "Multiple EAN's found for Electricity";
    ResponseMessages["MULTIPLE_EAN_GAS"] = "Multiple EAN's found for GAS";
    ResponseMessages["CAR_IS_DOWN"] = "CAR is Unavailable";
})(ResponseMessages || (ResponseMessages = {}));

class OffersGetResponseMapper {
    map(res) {
        const offers = res.payload || [];
        const response = offers.map(offer => ({
            campaign: offer.campaign,
            vatIncluded: offer.vat_included,
            incentive: offer.incentive && {
                id: offer.incentive.id,
                description: offer.incentive.description,
                code: offer.incentive.code,
                shortDescription: offer.incentive.short_description,
                discountPrice: offer.incentive.discount_price,
                distributionPlan: offer.incentive.distribution_plan
            },
            totalAmounts: {
                expectedYearlyAmount: offer.total_amounts.expected_yearly_amount,
                expectedMonthlyAmount: offer.total_amounts.expected_monthly_amount
            },
            offerOverviews: offer.offer_overviews?.map(offerOverview => {
                const overview = {
                    product: {
                        id: offerOverview.product.proposition_id,
                        description: offerOverview.product.proposition_description,
                        durationInYears: offerOverview.product.duration_in_years,
                        energyType: offerOverview.product.energy_type,
                        contractType: offerOverview.product.contract_type
                    },
                    totalPrice: {
                        expectedYearlyAmount: offerOverview.total_price.expected_yearly_amount,
                        expectedMonthlyAmount: offerOverview.total_price.expected_monthly_amount
                    },
                    financialIncentives: offerOverview.financial_incentives,
                    consumptionPrices: offerOverview.consumption_prices?.map(price => ({
                        amount: price.amount,
                        description: price.description,
                        fromDate: price.from_date,
                        toDate: price.to_date
                    })),
                    priceGroups: offerOverview.price_groups?.map(priceGroup => ({
                        id: priceGroup.id,
                        description: priceGroup.description,
                        prices: priceGroup.prices?.map(priceItem => ({
                            id: priceItem.id,
                            expectedYearlyAmount: 0,
                            expectedMonthlyAmount: 0,
                            expectedPeriodAmount: priceItem.expected_period_amount,
                            unit: priceItem.unit,
                            unitPrice: priceItem.unit_price,
                            description: priceItem.description,
                            fromDate: priceItem.from_date,
                            toDate: priceItem.to_date
                        })),
                        totalPrice: {
                            expectedYearlyAmount: priceGroup.total_price.expected_yearly_amount,
                            expectedMonthlyAmount: priceGroup.total_price.expected_monthly_amount
                        }
                    }))
                };
                return overview;
            })
        }));
        return response;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: OffersGetResponseMapper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: OffersGetResponseMapper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: OffersGetResponseMapper, decorators: [{
            type: Injectable
        }] });

class GetOffersService {
    constructor() {
        this.apiUrl = inject(API, { optional: true });
        this.httpClient = inject(HttpClient);
        this.getMapper = inject(OffersGetResponseMapper);
        this.apiVersion = '3';
        this.endpoint = `${this.apiUrl || ''}/contracting/newcustomer/offers/v${this.apiVersion}`;
    }
    call$(payload) {
        const params = toHttpParams(snakeCase(payload));
        const headers = { Accept: 'application/json' };
        return this.httpClient
            .get(this.endpoint, { params, headers })
            .pipe(map(response => this.getMapper.map(response)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: GetOffersService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: GetOffersService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: GetOffersService, decorators: [{
            type: Injectable
        }] });

const getOffersV3Api = createNamedApi('[ConsentsState] Get Offers V3', GetOffersService);

const apiVersion$e = '1';
const getOrganisationDetailsApi = createServiceApi('[NewCustomerState] Get organisation details', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/organisationdetails/v${apiVersion$e}`,
    version: apiVersion$e,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$d = '1';
const putOrganisationDetailsApi = createServiceApi('[NewCustomerState] Put organisation details', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/organisationdetails/v${apiVersion$d}`,
    version: apiVersion$d,
    getBody: payload => payload
});

const apiVersion$c = '2';
const getPaymentDetailsApi = createServiceApi('[NewCustomerState] Get payment details', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/paymentdetails/v${apiVersion$c}`,
    version: apiVersion$c,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$b = '2';
const putPaymentDetailsApi = createServiceApi('[NewCustomerState] Put payment details', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/paymentdetails/v${apiVersion$b}`,
    version: apiVersion$b,
    getBody: payload => payload
});

const apiVersion$a = '2';
const getPersonalDetailsApi = createServiceApi('[NewCustomerState] Get personal details', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/personaldetails/v${apiVersion$a}`,
    version: apiVersion$a,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$9 = '2';
const putPersonalDetailsApi = createServiceApi('[NewCustomerState] Put personal details', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/personaldetails/v${apiVersion$9}`,
    version: apiVersion$9,
    getBody: payload => payload
});

const apiVersion$8 = '2';
const getPropositionApi = createServiceApi('[NewCustomerState] Get proposition', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/proposition/v${apiVersion$8}`,
    version: apiVersion$8,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$7 = '3';
const putPropositionApi = createServiceApi('[NewCustomerState] Put proposition', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/proposition/v${apiVersion$7}`,
    version: apiVersion$7,
    getBody: payload => payload
});

const apiVersion$6 = '1';
const getRunningProcessesApi = createServiceApi('[NewCustomerState] Get running processes', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/activecases/v${apiVersion$6}`,
    version: apiVersion$6,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$5 = '2';
const getStartDateApi = createServiceApi('[NewCustomerState] Get start date', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/startdate/v${apiVersion$5}`,
    version: apiVersion$5,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$4 = '2';
const putStartDateApi = createServiceApi('[NewCustomerState] Put start date', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/startdate/v${apiVersion$4}`,
    version: apiVersion$4,
    getBody: payload => payload
});

const apiVersion$3 = '2';
const getSummaryApi = createServiceApi('[NewCustomerState] Get summary', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/summary/v${apiVersion$3}`,
    version: apiVersion$3,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$2 = '2';
const getSupplyAddressApi = createServiceApi('[NewCustomerState] Get supply address', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/supplyaddress/v${apiVersion$2}`,
    version: apiVersion$2,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const apiVersion$1 = '2';
const putSupplyAddressApi = createServiceApi('[NewCustomerState] Put supply address', 'put', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/supplyaddress/v${apiVersion$1}`,
    version: apiVersion$1,
    getBody: ({ flowId, ...payload }) => ({ flow_id: flowId, ...payload }),
    getHeaders: payload => mapWhenDefined(payload.flowId, flow_id => ({ flow_id })) ?? {}
});

const apiVersion = '1';
const getVoucherApi = createServiceApi('[NewCustomerState] Get voucher', 'get', serviceTypes(), {
    getUrl: `/${NEW_CUSTOMER_PATH}/voucher/v${apiVersion}`,
    version: apiVersion,
    getParams: payload => payload,
    extractFromNestedKey: 'payload'
});

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(getSupplyAddressApi, putSupplyAddressApi, getConsumptionApi, putConsumptionApi, getOffersApi, getOffersV3Api, getCampaignsApi, getPropositionApi, putPropositionApi, getPersonalDetailsApi, putPersonalDetailsApi, getCorrespondenceDetailsApi, putCorrespondenceDetailsApi, getPaymentDetailsApi, putPaymentDetailsApi, getSummaryApi, putConfirmationApi, getAdditionalInformationApi, putAdditionalInformationApi, getBudgetBillApi, putBudgetBillApi, getInstallationsApi, putEanDetailsApi, getConsentApi, putConsentApi, getMarketConsumptionApi, getStartDateApi, putStartDateApi, getDepositApi, updateDepositApi, getCreditCheckConsentApi, updateCreditCheckConsentApi, getDuplicateCustomerApi, putDuplicateCustomerApi, getRunningProcessesApi, getOrganisationDetailsApi, putOrganisationDetailsApi, getMetaDataApi, putMetaDataApi, putExtendTimeToLiveApi, getVoucherApi);
const MAPPERS = [OffersGetResponseMapper];
class NewCustomerCoreModule {
    constructor() {
        const parentModule = inject(NewCustomerCoreModule, { optional: true, skipSelf: true });
        if (parentModule) {
            throw new Error('NewCustomerCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: NewCustomerCoreModule,
            providers: [
                {
                    provide: API,
                    useValue: config ? config.api : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: NewCustomerCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "20.2.4", ngImport: i0, type: NewCustomerCoreModule, imports: [i1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: NewCustomerCoreModule, providers: [...MAPPERS, ...PROVIDERS], imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.2.4", ngImport: i0, type: NewCustomerCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: [...MAPPERS, ...PROVIDERS]
                }]
        }], ctorParameters: () => [] });

const { getSupplyAddressActions, getSupplyAddress, getSupplyAddressSuccess, getSupplyAddressError, getSupplyAddressClear } = getSupplyAddressApi;
const { putSupplyAddressActions, putSupplyAddress, putSupplyAddressSuccess, putSupplyAddressError, putSupplyAddressClear } = putSupplyAddressApi;

var SubSegmentType;
(function (SubSegmentType) {
    SubSegmentType["HOUSING_ASSOCIATION"] = "housing_association";
    SubSegmentType["PROJECT_CONTRACTORS"] = "project_contractors";
    SubSegmentType["OWNERS_ASSOCIATION"] = "owners_association";
    SubSegmentType["COMMERCIAL_RENTAL"] = "commercial_rental";
    SubSegmentType["HOUSING_ASSOCIATION_IS"] = "housing_association_intermediate_supplier";
    SubSegmentType["PROJECT_CONTRACTORS_ID"] = "project_contractors_intermediate_delivery";
    SubSegmentType["COMMERCIAL_RENTAL_IS"] = "commercial_rental_intermediate_supplier";
    SubSegmentType["SME_SINGLE_SITE"] = "sme_single_site";
    SubSegmentType["SME_MULTI_SITE"] = "sme_multi_site";
    SubSegmentType["SOHO"] = "soho";
    SubSegmentType["SPEC_MULTI_SITE"] = "spec_multi_site";
    SubSegmentType["PURCHASING_COMBINATION"] = "purchasing_combination";
    SubSegmentType["GOVERNMENT"] = "government";
})(SubSegmentType || (SubSegmentType = {}));

var SupplyAddressMessageCodes;
(function (SupplyAddressMessageCodes) {
    SupplyAddressMessageCodes["NOT_FOUND"] = "400";
})(SupplyAddressMessageCodes || (SupplyAddressMessageCodes = {}));

const { getOffersActions, getOffers, getOffersSuccess, getOffersError, getOffersClear } = getOffersApi;

const { getOffersV3Actions, getOffersV3, getOffersV3Success, getOffersV3Error, getOffersV3Clear } = getOffersV3Api;

const { getPropositionActions, getProposition, getPropositionSuccess, getPropositionError, getPropositionClear } = getPropositionApi;
const { putPropositionActions, putProposition, putPropositionSuccess, putPropositionError, putPropositionClear } = putPropositionApi;

const { getCampaignsActions, getCampaigns, getCampaignsSuccess, getCampaignsError, getCampaignsClear } = getCampaignsApi;

const { getPersonalDetailsActions, getPersonalDetails, getPersonalDetailsSuccess, getPersonalDetailsError, getPersonalDetailsClear } = getPersonalDetailsApi;
const { putPersonalDetailsActions, putPersonalDetails, putPersonalDetailsSuccess, putPersonalDetailsError, putPersonalDetailsClear } = putPersonalDetailsApi;

const { getCorrespondenceDetailsActions, getCorrespondenceDetails, getCorrespondenceDetailsSuccess, getCorrespondenceDetailsError, getCorrespondenceDetailsClear } = getCorrespondenceDetailsApi;
const { putCorrespondenceDetailsActions, putCorrespondenceDetails, putCorrespondenceDetailsSuccess, putCorrespondenceDetailsError, putCorrespondenceDetailsClear } = putCorrespondenceDetailsApi;

const { getAdditionalInformationActions, getAdditionalInformation, getAdditionalInformationSuccess, getAdditionalInformationError, getAdditionalInformationClear } = getAdditionalInformationApi;
const { putAdditionalInformationActions, putAdditionalInformation, putAdditionalInformationSuccess, putAdditionalInformationError, putAdditionalInformationClear } = putAdditionalInformationApi;

var AdditionalInformationType;
(function (AdditionalInformationType) {
    AdditionalInformationType["RESIDENTIAL"] = "residential";
    AdditionalInformationType["CONTRACT_WITH_ANOTHER_SUPPLIER"] = "contract_with_another_supplier";
    AdditionalInformationType["COMPLEX"] = "complex";
})(AdditionalInformationType || (AdditionalInformationType = {}));

const { getConsumptionActions, getConsumption, getConsumptionSuccess, getConsumptionError, getConsumptionClear } = getConsumptionApi;
const { putConsumptionActions, putConsumption, putConsumptionSuccess, putConsumptionError, putConsumptionClear } = putConsumptionApi;

const { getPaymentDetailsActions, getPaymentDetails, getPaymentDetailsSuccess, getPaymentDetailsError, getPaymentDetailsClear } = getPaymentDetailsApi;
const { putPaymentDetailsActions, putPaymentDetails, putPaymentDetailsSuccess, putPaymentDetailsError, putPaymentDetailsClear } = putPaymentDetailsApi;

const { putEanDetailsActions, putEanDetails, putEanDetailsSuccess, putEanDetailsError, putEanDetailsClear } = putEanDetailsApi;

const { getInstallationsActions, getInstallations, getInstallationsSuccess, getInstallationsError, getInstallationsClear } = getInstallationsApi;

var InstallationsMessageCodes;
(function (InstallationsMessageCodes) {
    InstallationsMessageCodes["NOT_FOUND"] = "400";
})(InstallationsMessageCodes || (InstallationsMessageCodes = {}));

const { getSummaryActions, getSummary, getSummarySuccess, getSummaryError, getSummaryClear } = getSummaryApi;

const { putConfirmationActions, putConfirmation, putConfirmationSuccess, putConfirmationError, putConfirmationClear } = putConfirmationApi;

const { getConsentActions, getConsent, getConsentSuccess, getConsentError, getConsentClear } = getConsentApi;
const { putConsentActions, putConsent, putConsentSuccess, putConsentError, putConsentClear } = putConsentApi;

var ConsentMessageCodes;
(function (ConsentMessageCodes) {
    ConsentMessageCodes["MISSING_CUSTOMER_KEY"] = "1017";
    ConsentMessageCodes["INVALID_CUSTOMER_KEY"] = "1018";
    ConsentMessageCodes["REGISTERS_REQUEST_BLOCKED"] = "1015";
    ConsentMessageCodes["REGISTERS_NOT_ACCESSIBLE"] = "1023";
    ConsentMessageCodes["NOT_APPLICABLE"] = "1008";
    ConsentMessageCodes["NO_ADDITIONAL_INFO_AVAILABLE"] = "1007";
})(ConsentMessageCodes || (ConsentMessageCodes = {}));

const { getMarketConsumptionActions, getMarketConsumption, getMarketConsumptionSuccess, getMarketConsumptionError, getMarketConsumptionClear } = getMarketConsumptionApi;

const { getBudgetBillActions, getBudgetBill, getBudgetBillSuccess, getBudgetBillError, getBudgetBillClear } = getBudgetBillApi;
const { putBudgetBillActions, putBudgetBill, putBudgetBillSuccess, putBudgetBillError, putBudgetBillClear } = putBudgetBillApi;

const { getStartDateActions, getStartDate, getStartDateSuccess, getStartDateError, getStartDateClear } = getStartDateApi;
const { putStartDateActions, putStartDate, putStartDateSuccess, putStartDateError, putStartDateClear } = putStartDateApi;

var PossibleStartDateKey;
(function (PossibleStartDateKey) {
    PossibleStartDateKey["COOLDOWN"] = "cooldown";
    PossibleStartDateKey["WITHOUT_COOLDOWN"] = "without_cooldown";
    PossibleStartDateKey["SUGGESTED"] = "suggested";
})(PossibleStartDateKey || (PossibleStartDateKey = {}));

const { getDepositActions, getDeposit, getDepositSuccess, getDepositError, getDepositClear } = getDepositApi;
const { updateDepositActions, updateDeposit, updateDepositSuccess, updateDepositError, updateDepositClear } = updateDepositApi;

var DepositRejectionReasons;
(function (DepositRejectionReasons) {
    DepositRejectionReasons["EXTRA_ADDRESS"] = "EXTRA_ADDRESS";
    DepositRejectionReasons["GAP"] = "GAP";
    DepositRejectionReasons["CANCELLATION"] = "CANCELLATION";
    DepositRejectionReasons["CURATOR"] = "CURATOR";
    DepositRejectionReasons["NOT_JUSTIFIED"] = "NOT_JUSTIFIED";
    DepositRejectionReasons["POST_MORTEM"] = "POST_MORTEM";
    DepositRejectionReasons["CAMPING"] = "CAMPING";
    DepositRejectionReasons["PARTNER_AGREED"] = "PARTNER_AGREED";
    DepositRejectionReasons["DEPOSIT_PAID"] = "DEPOSIT_PAID";
    DepositRejectionReasons["SIGNUP_AFTER_CANCELLATION"] = "SIGNUP_AFTER_CANCELLATION";
    DepositRejectionReasons["DELIVERY_GUARANTEE"] = "DELIVERY_GUARANTEE";
    DepositRejectionReasons["COACH_AGREED"] = "COACH_AGREED";
})(DepositRejectionReasons || (DepositRejectionReasons = {}));

const { getCreditCheckConsentActions, getCreditCheckConsent, getCreditCheckConsentSuccess, getCreditCheckConsentError, getCreditCheckConsentClear } = getCreditCheckConsentApi;
const { updateCreditCheckConsentActions, updateCreditCheckConsent, updateCreditCheckConsentSuccess, updateCreditCheckConsentError, updateCreditCheckConsentClear } = updateCreditCheckConsentApi;

var CreditCheckConsentStatus;
(function (CreditCheckConsentStatus) {
    CreditCheckConsentStatus["GIVEN"] = "given";
    CreditCheckConsentStatus["REJECTED"] = "rejected";
})(CreditCheckConsentStatus || (CreditCheckConsentStatus = {}));

const { getDuplicateCustomerActions, getDuplicateCustomer, getDuplicateCustomerSuccess, getDuplicateCustomerError, getDuplicateCustomerClear } = getDuplicateCustomerApi;
const { putDuplicateCustomerActions, putDuplicateCustomer, putDuplicateCustomerSuccess, putDuplicateCustomerError, putDuplicateCustomerClear } = putDuplicateCustomerApi;

const { getRunningProcessesActions, getRunningProcesses, getRunningProcessesSuccess, getRunningProcessesError, getRunningProcessesClear } = getRunningProcessesApi;

var ProcessStatus;
(function (ProcessStatus) {
    ProcessStatus["IN_PROGRESS"] = "INPROGRESS";
    ProcessStatus["COMPLETED"] = "COMPLETED";
})(ProcessStatus || (ProcessStatus = {}));
var Source;
(function (Source) {
    Source["IPROCESS"] = "iProcess";
    Source["AMXBPM"] = "AMXBPM";
})(Source || (Source = {}));

const { getOrganisationDetailsActions, getOrganisationDetails, getOrganisationDetailsSuccess, getOrganisationDetailsError, getOrganisationDetailsClear } = getOrganisationDetailsApi;
const { putOrganisationDetailsActions, putOrganisationDetails, putOrganisationDetailsSuccess, putOrganisationDetailsError, putOrganisationDetailsClear } = putOrganisationDetailsApi;

const { getMetaDataActions, getMetaData, getMetaDataSuccess, getMetaDataError, getMetaDataClear } = getMetaDataApi;
const { putMetaDataActions, putMetaData, putMetaDataSuccess, putMetaDataError, putMetaDataClear } = putMetaDataApi;

const { putExtendTimeToLiveActions, putExtendTimeToLive, putExtendTimeToLiveSuccess, putExtendTimeToLiveError, putExtendTimeToLiveClear } = putExtendTimeToLiveApi;

const { getVoucherActions, getVoucher, getVoucherSuccess, getVoucherError, getVoucherClear } = getVoucherApi;

/**
 * Generated bundle index. Do not edit.
 */

export { API, AdditionalInformationType, ChoiceOfTariff, ConsentMessageCodes, ConsentStatus, CreditCheckConsentStatus, DepositRejectionReasons, InstallationsMessageCodes, NewCustomerCoreModule, PossibleStartDateKey, ProcessStatus, ResponseCodes, ResponseMessages, Source, SubSegmentType, SupplyAddressMessageCodes, UsageType, getAdditionalInformation, getAdditionalInformationActions, getAdditionalInformationClear, getAdditionalInformationError, getAdditionalInformationSuccess, getBudgetBill, getBudgetBillActions, getBudgetBillClear, getBudgetBillError, getBudgetBillSuccess, getCampaigns, getCampaignsActions, getCampaignsClear, getCampaignsError, getCampaignsSuccess, getConsent, getConsentActions, getConsentClear, getConsentError, getConsentSuccess, getConsumption, getConsumptionActions, getConsumptionClear, getConsumptionError, getConsumptionSuccess, getCorrespondenceDetails, getCorrespondenceDetailsActions, getCorrespondenceDetailsClear, getCorrespondenceDetailsError, getCorrespondenceDetailsSuccess, getCreditCheckConsent, getCreditCheckConsentActions, getCreditCheckConsentClear, getCreditCheckConsentError, getCreditCheckConsentSuccess, getDeposit, getDepositActions, getDepositClear, getDepositError, getDepositSuccess, getDuplicateCustomer, getDuplicateCustomerActions, getDuplicateCustomerClear, getDuplicateCustomerError, getDuplicateCustomerSuccess, getInstallations, getInstallationsActions, getInstallationsClear, getInstallationsError, getInstallationsSuccess, getMarketConsumption, getMarketConsumptionActions, getMarketConsumptionClear, getMarketConsumptionError, getMarketConsumptionSuccess, getMetaData, getMetaDataActions, getMetaDataClear, getMetaDataError, getMetaDataSuccess, getOffers, getOffersActions, getOffersClear, getOffersError, getOffersSuccess, getOffersV3, getOffersV3Actions, getOffersV3Clear, getOffersV3Error, getOffersV3Success, getOrganisationDetails, getOrganisationDetailsActions, getOrganisationDetailsClear, getOrganisationDetailsError, getOrganisationDetailsSuccess, getPaymentDetails, getPaymentDetailsActions, getPaymentDetailsClear, getPaymentDetailsError, getPaymentDetailsSuccess, getPersonalDetails, getPersonalDetailsActions, getPersonalDetailsClear, getPersonalDetailsError, getPersonalDetailsSuccess, getProposition, getPropositionActions, getPropositionClear, getPropositionError, getPropositionSuccess, getRunningProcesses, getRunningProcessesActions, getRunningProcessesClear, getRunningProcessesError, getRunningProcessesSuccess, getStartDate, getStartDateActions, getStartDateClear, getStartDateError, getStartDateSuccess, getSummary, getSummaryActions, getSummaryClear, getSummaryError, getSummarySuccess, getSupplyAddress, getSupplyAddressActions, getSupplyAddressClear, getSupplyAddressError, getSupplyAddressSuccess, getVoucher, getVoucherActions, getVoucherClear, getVoucherError, getVoucherSuccess, putAdditionalInformation, putAdditionalInformationActions, putAdditionalInformationClear, putAdditionalInformationError, putAdditionalInformationSuccess, putBudgetBill, putBudgetBillActions, putBudgetBillClear, putBudgetBillError, putBudgetBillSuccess, putConfirmation, putConfirmationActions, putConfirmationClear, putConfirmationError, putConfirmationSuccess, putConsent, putConsentActions, putConsentClear, putConsentError, putConsentSuccess, putConsumption, putConsumptionActions, putConsumptionClear, putConsumptionError, putConsumptionSuccess, putCorrespondenceDetails, putCorrespondenceDetailsActions, putCorrespondenceDetailsClear, putCorrespondenceDetailsError, putCorrespondenceDetailsSuccess, putDuplicateCustomer, putDuplicateCustomerActions, putDuplicateCustomerClear, putDuplicateCustomerError, putDuplicateCustomerSuccess, putEanDetails, putEanDetailsActions, putEanDetailsClear, putEanDetailsError, putEanDetailsSuccess, putExtendTimeToLive, putExtendTimeToLiveActions, putExtendTimeToLiveClear, putExtendTimeToLiveError, putExtendTimeToLiveSuccess, putMetaData, putMetaDataActions, putMetaDataClear, putMetaDataError, putMetaDataSuccess, putOrganisationDetails, putOrganisationDetailsActions, putOrganisationDetailsClear, putOrganisationDetailsError, putOrganisationDetailsSuccess, putPaymentDetails, putPaymentDetailsActions, putPaymentDetailsClear, putPaymentDetailsError, putPaymentDetailsSuccess, putPersonalDetails, putPersonalDetailsActions, putPersonalDetailsClear, putPersonalDetailsError, putPersonalDetailsSuccess, putProposition, putPropositionActions, putPropositionClear, putPropositionError, putPropositionSuccess, putStartDate, putStartDateActions, putStartDateClear, putStartDateError, putStartDateSuccess, putSupplyAddress, putSupplyAddressActions, putSupplyAddressClear, putSupplyAddressError, putSupplyAddressSuccess, updateCreditCheckConsent, updateCreditCheckConsentActions, updateCreditCheckConsentClear, updateCreditCheckConsentError, updateCreditCheckConsentSuccess, updateDeposit, updateDepositActions, updateDepositClear, updateDepositError, updateDepositSuccess };
